import { useMutation } from '@src/query';


/**
 * Settings → Billing mutation hooks (T10). Each wraps the shared `useMutation`
 * engine with the injected-`queryClient` `mutateFn({ apiClient, queryClient })`
 * contract — the corelib query singleton is NOT imported here. Invalidation runs
 * inside `mutateFn`; the caller passes both the billing `queryKey` and the org
 * `mineKey` it already holds (a plan change invalidates the org too), so the
 * exact cache keys are preserved unchanged.
 *
 * Mirrors `query/garage/useDonorMutations.js`.
 */

function refresh(queryClient, queryKey, mineKey) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey }),
    queryClient.invalidateQueries({ queryKey: mineKey }),
  ]);
}


/** Switch subscription plan (demo billing — activates instantly). Variables:
 * `{ sellerId, plan, queryKey, mineKey }`. */
function useBillingPlanMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, plan, queryKey, mineKey }) => apiClient
      .request({ url: `/sellers/${sellerId}/billing/plan/`, method: 'POST', payload: { plan } })
      .then((data) => refresh(queryClient, queryKey, mineKey).then(() => data)),
    ...params,
  });
}


/** Start a Stripe subscription for a paid plan (M34). Variables: `{ sellerId,
 * plan }`. Resolves to `{ client_secret, subscription_id }` — the Payment
 * Element confirms `client_secret`. Deliberately does NOT invalidate the billing
 * cache: activation is webhook-driven, so `Billing.jsx` polls `useBillingQuery`
 * after the card is confirmed rather than trusting an optimistic refetch. */
function useBillingSubscribeMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId, plan }) => apiClient
      .request({ url: `/sellers/${sellerId}/billing/subscribe/`, method: 'POST', payload: { plan } }),
    ...params,
  });
}


/** Cancel the Stripe subscription (M34) — schedules cancellation at period end.
 * Variables: `{ sellerId, queryKey, mineKey }`. Returns the refreshed billing
 * payload; invalidates the billing + org caches so the "Cancels on {date}" banner
 * (driven by `cancel_at_period_end`) picks up immediately. */
function useBillingCancelMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, queryKey, mineKey }) => apiClient
      .request({ url: `/sellers/${sellerId}/billing/cancel/`, method: 'POST' })
      .then((data) => refresh(queryClient, queryKey, mineKey).then(() => data)),
    ...params,
  });
}


/** Start an update-card flow (M34). Variables: `{ sellerId }`. Resolves to
 * `{ client_secret }` for a SetupIntent the Payment Element confirms in
 * `mode="setup"`. Deliberately does NOT invalidate — the new card is read back on
 * the next billing fetch the caller triggers after the modal completes. */
function useBillingPaymentMethodMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, sellerId }) => apiClient
      .request({ url: `/sellers/${sellerId}/billing/payment-method/`, method: 'POST' }),
    ...params,
  });
}


/** Toggle the interchange (fitment) add-on. Variables: `{ sellerId, enabled,
 * queryKey, mineKey }`. */
function useBillingInterchangeMutation(params) {
  return useMutation({
    mutateFn: ({ apiClient, queryClient, sellerId, enabled, queryKey, mineKey }) => apiClient
      .request({ url: `/sellers/${sellerId}/billing/interchange/`, method: 'POST', payload: { enabled } })
      .then((data) => refresh(queryClient, queryKey, mineKey).then(() => data)),
    ...params,
  });
}


export {
  useBillingCancelMutation,
  useBillingInterchangeMutation,
  useBillingPaymentMethodMutation,
  useBillingPlanMutation,
  useBillingSubscribeMutation,
};
